import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap";

const ComplaintDetailModal = ({ isOpen, toggle, complaint, onViewUser, onEdit }) => {
  const getStateBadge = (stateValue) => {
    const stateLower = stateValue?.toLowerCase();
    switch (stateLower) {
      case "raised":
        return <Badge color="warning" className="px-3 py-2">Raised</Badge>;
      case "inprogress":
        return <Badge color="info" className="px-3 py-2">In Progress</Badge>;
      case "testing":
        return <Badge color="primary" className="px-3 py-2">Testing</Badge>;
      case "done":
        return <Badge color="success" className="px-3 py-2">Done</Badge>;
      default:
        return <Badge color="secondary" className="px-3 py-2">{stateValue || "N/A"}</Badge>;
    }
  };

  const getTypeBadge = (typeValue) => {
    switch (typeValue) {
      case "Report bug":
        return <Badge color="danger" className="px-3 py-2">Report bug</Badge>;
      case "Suggest tag":
        return <Badge color="info" className="px-3 py-2">Suggest tag</Badge>;
      case "Feedback":
        return <Badge color="success" className="px-3 py-2">Feedback</Badge>;
      case "Help":
        return <Badge color="primary" className="px-3 py-2">Help</Badge>;
      default:
        return <Badge color="secondary" className="px-3 py-2">{typeValue || "N/A"}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!complaint) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="bg-primary text-white">
        <i className="mdi mdi-help-circle me-2"></i>Complaint Details
        {complaint.id && <span className="ms-2 small">(ID: {complaint.id})</span>}
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Card className="shadow-sm mb-3">
          <CardBody className="p-4">
            <Row className="g-3">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 fw-bold">
                    <i className="mdi mdi-information me-2"></i>Basic Information
                  </h6>
                  <div className="d-flex gap-2">
                    {getTypeBadge(complaint.complaintType || complaint.type)}
                    {getStateBadge(complaint.state)}
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Complaint ID</label>
                  <div className="font-monospace fw-semibold">{complaint.id || "-"}</div>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Type ID</label>
                  <div className="fw-semibold">{complaint.type_id || "-"}</div>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Category</label>
                  <div className="fw-semibold">{complaint.category || "Not Defined"}</div>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">State</label>
                  <div>{getStateBadge(complaint.state)}</div>
                </div>
              </Col>

              {complaint.sideNote && (
                <Col xs={12}>
                  <div className="mb-3">
                    <label className="form-label text-muted small mb-1">Side Note</label>
                    <div className="p-3 bg-light rounded border">
                      {complaint.sideNote}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        <Card className="shadow-sm mb-3">
          <CardBody className="p-4">
            <h6 className="mb-3 fw-bold">
              <i className="mdi mdi-message-text me-2"></i>Message
            </h6>
            <div className="p-3 bg-light rounded border" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {complaint.message || "No message provided"}
            </div>
          </CardBody>
        </Card>

        {complaint.user && (
          <Card className="shadow-sm mb-3">
            <CardBody className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold">
                  <i className="mdi mdi-account me-2"></i>User Information
                </h6>
                {complaint.user.userId && onViewUser && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => onViewUser(complaint.user.userId)}
                    className="px-3"
                  >
                    <i className="mdi mdi-eye me-1"></i>View User Details
                  </Button>
                )}
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">Username</label>
                    <div className="fw-semibold">{complaint.user.username || "-"}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">User ID</label>
                    <div className="text-muted small font-monospace">{complaint.user.userId || "-"}</div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        <Card className="shadow-sm">
          <CardBody className="p-4">
            <h6 className="mb-3 fw-bold">
              <i className="mdi mdi-clock-outline me-2"></i>Timeline
            </h6>
            <Row className="g-3">
              <Col md={6}>
                <div className="mb-2">
                  <label className="form-label text-muted small mb-1">Created At</label>
                  <div className="small">{formatDate(complaint.created_at)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <label className="form-label text-muted small mb-1">Updated At</label>
                  <div className="small">{formatDate(complaint.updated_at)}</div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        {onEdit && (
          <Button color="primary" onClick={() => onEdit(complaint)} className="me-2">
            <i className="mdi mdi-pencil me-2"></i>Edit
          </Button>
        )}
        <Button color="secondary" onClick={toggle}>
          <i className="mdi mdi-close me-2"></i>Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ComplaintDetailModal;

